// Copied from original/src/components/PartnerOfficeSelector.tsx:1-407 (ADR-08 §2.1 "copy, then cut logic").
// Kept verbatim: trigger button (:92-188, selected / empty states, rotating chevron), backdrop + panel (:191-201),
// live search bar with clear button (:202-231), the three filter pills with count badges (:233-300), the list with
// the empty state (:303-401), the click-outside listener (:30-44) and the `filterType` pill state (:27, pure UI).
// Cut (data lookups → props): `partnerOffices: Debt[]` + `selectedOffice` lookup by parseInt(id) (:47-51) →
// `options` / `selected`; `counts` over the full list (:53-66) → `counts` prop (integers, from the container);
// the search filter over name / phone / id / accountNumber / address (:68-87) → `searchQuery` / `onSearchChange`,
// the container filters `options`; `formatNumber(o.amount)` / `amount_usd !== 0` (:155-162, :388-395) →
// `balanceLabel` / `usdBalanceLabel` (formatted by the container, ADR-02 §2.9); the `accNumDisplay` rule
// (:323-326, accountNumber vs 1000+id) → `accountLabel`; `o.type === 'دين'` → `kind: 'debt' | 'trust'`;
// `lang` prop → `t()`. Added: data-testid attributes.
import { useEffect, useRef, useState } from 'react';
import { Search, X, ChevronDown, Building2, User, Phone, Check } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface PartnerOfficeOption {
  readonly id: string;
  /** The office / customer name. */
  readonly label: string;
  /** Old `Debt.type`: 'دين' → 'debt' (red, "لنا"), 'أمانة' → 'trust' (green, "علينا"). */
  readonly kind: 'debt' | 'trust';
  readonly phone?: string | null;
  /** Shown as `#{accountLabel}`; the old app showed the account number, or the id when it was the default 1000+id. */
  readonly accountLabel: string;
  /** IQD balance, formatted by the container (e.g. "1,250,000"). */
  readonly balanceLabel: string;
  /** USD balance formatted by the container; null / absent hides the amber line (old: `amount_usd !== 0`). */
  readonly usdBalanceLabel?: string | null;
}

export interface PartnerOfficeCounts {
  readonly all: number;
  readonly debt: number;
  readonly trust: number;
}

export interface PartnerOfficeSelectorProps {
  /** The options to list; the container applies `searchQuery` to them. */
  readonly options: readonly PartnerOfficeOption[];
  /** The selected option (passed separately so it survives the container's search filter); null = none. */
  readonly selected: PartnerOfficeOption | null;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  /** Counts over the unfiltered list, for the pill badges. Defaults to counting `options`. */
  readonly counts?: PartnerOfficeCounts;
  readonly searchQuery: string;
  readonly onSearchChange: (query: string) => void;
  readonly isOpen: boolean;
  readonly setIsOpen: (open: boolean) => void;
  readonly onCloseOtherDropdowns?: () => void;
}

export function PartnerOfficeSelector({
  options,
  selected,
  selectedId,
  onSelect,
  counts,
  searchQuery,
  onSearchChange,
  isOpen,
  setIsOpen,
  onCloseOtherDropdowns,
}: PartnerOfficeSelectorProps) {
  const t = useT();
  const [filterType, setFilterType] = useState<'all' | 'debt' | 'trust'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const selectedOffice = selected;

  // Total counts for badge pills (integers, not money)
  const pillCounts: PartnerOfficeCounts = counts ?? {
    all: options.length,
    debt: options.filter((o) => o.kind === 'debt').length,
    trust: options.filter((o) => o.kind === 'trust').length,
  };

  // Category pill filter (pure UI); the text search is applied by the container
  const filteredOffices = options.filter((o) => {
    if (filterType === 'debt' && o.kind !== 'debt') return false;
    if (filterType === 'trust' && o.kind !== 'trust') return false;
    return true;
  });

  return (
    <div className="relative w-full text-right font-sans" ref={dropdownRef} data-testid="partner-office-selector">
      {/* Trigger Button */}
      <button
        type="button"
        id="partner-office-selector-trigger"
        data-testid="partner-office-trigger"
        onClick={() => {
          if (!isOpen && onCloseOtherDropdowns) {
            onCloseOtherDropdowns();
          }
          setIsOpen(!isOpen);
        }}
        className={`w-full bg-slate-50 dark:bg-slate-900/50 border rounded-2xl px-3.5 md:px-5 py-3 md:py-3.5 outline-none transition-all duration-200 text-right flex items-center justify-between shadow-inner min-h-[58px] ${
          isOpen
            ? 'border-primary-500 ring-4 ring-primary-500/15'
            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
        }`}
      >
        {selectedOffice ? (
          <div className="flex items-center justify-between w-full gap-2.5 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-8 h-8 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0">
                <Building2 size={16} />
              </div>
              <div className="flex flex-col min-w-0 text-right">
                <div className="flex items-center gap-2 truncate">
                  <span className="font-black text-xs sm:text-sm text-slate-900 dark:text-white truncate">
                    {selectedOffice.label}
                  </span>
                  <span
                    className={`text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-md shrink-0 border ${
                      selectedOffice.kind === 'debt'
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200 dark:border-rose-900'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900'
                    }`}
                  >
                    {selectedOffice.kind === 'debt' ? t('partner.debt_to_us') : t('partner.trust_by_us')}
                  </span>
                </div>
                {selectedOffice.phone && (
                  <span className="text-[10px] font-mono text-slate-400 truncate">
                    📞 {selectedOffice.phone}
                  </span>
                )}
              </div>
            </div>

            {/* Display Balances inside trigger */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-left font-mono">
                <div className="text-[9px] text-slate-400 font-bold">
                  {t('partner.current_balance')}
                </div>
                <div
                  className={`text-xs font-black ${
                    selectedOffice.kind === 'debt'
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {selectedOffice.balanceLabel}{' '}
                  <span className="text-[10px] font-medium">د.ع</span>
                </div>
                {selectedOffice.usdBalanceLabel && (
                  <div className="text-[10px] font-black text-amber-600 dark:text-amber-400">
                    ${selectedOffice.usdBalanceLabel}
                  </div>
                )}
              </div>
              <ChevronDown
                size={18}
                className={`text-slate-400 shrink-0 transition-transform duration-200 ${
                  isOpen ? 'rotate-180 text-primary-500' : ''
                }`}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2.5 text-slate-400 dark:text-slate-500">
              <Building2 size={18} className="text-slate-400" />
              <span className="text-xs sm:text-sm font-bold">
                {t('partner.select_placeholder')}
              </span>
            </div>
            <ChevronDown
              size={18}
              className={`text-slate-400 shrink-0 transition-transform duration-200 ${
                isOpen ? 'rotate-180 text-primary-500' : ''
              }`}
            />
          </div>
        )}
      </button>

      {/* Popover / Dropdown Panel */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            id="partner-office-dropdown-panel"
            data-testid="partner-office-panel"
            className="absolute z-50 mt-2 w-full bg-white dark:bg-[#161b22] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-fadeIn text-right"
            style={{ maxWidth: '100%' }}
          >
            {/* 1. Live Search Bar */}
            <div className="p-3.5 bg-slate-50/80 dark:bg-[#0d1117]/80 backdrop-blur-md border-b border-slate-150 dark:border-slate-800 space-y-2.5">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  id="partner-office-search-input"
                  data-testid="partner-office-search"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={t('partner.search_placeholder')}
                  className="w-full pl-9 pr-10 py-2.5 bg-white dark:bg-[#161b22] border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-black text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => onSearchChange('')}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* 2. Filter Pills: الكل / الديون (لنا) / الأمانات (علينا) */}
              <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                <button
                  type="button"
                  id="partner-filter-all"
                  onClick={() => setFilterType('all')}
                  className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-black transition-all flex items-center justify-center gap-1.5 ${
                    filterType === 'all'
                      ? 'bg-white dark:bg-[#161b22] text-slate-900 dark:text-white shadow-xs'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
                >
                  <span>{t('partner.filter_all')}</span>
                  <span
                    className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                      filterType === 'all'
                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                        : 'bg-slate-200 dark:bg-slate-700/60 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {pillCounts.all}
                  </span>
                </button>

                <button
                  type="button"
                  id="partner-filter-debt"
                  onClick={() => setFilterType('debt')}
                  className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-black transition-all flex items-center justify-center gap-1.5 ${
                    filterType === 'debt'
                      ? 'bg-rose-500 text-white shadow-xs'
                      : 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                  }`}
                >
                  <span>{t('partner.filter_debts')}</span>
                  <span
                    className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                      filterType === 'debt'
                        ? 'bg-white/25 text-white'
                        : 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'
                    }`}
                  >
                    {pillCounts.debt}
                  </span>
                </button>

                <button
                  type="button"
                  id="partner-filter-trust"
                  onClick={() => setFilterType('trust')}
                  className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-black transition-all flex items-center justify-center gap-1.5 ${
                    filterType === 'trust'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
                  }`}
                >
                  <span>{t('partner.filter_trusts')}</span>
                  <span
                    className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                      filterType === 'trust'
                        ? 'bg-white/25 text-white'
                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                    }`}
                  >
                    {pillCounts.trust}
                  </span>
                </button>
              </div>
            </div>

            {/* 3. Partner List with Prominent Client Balances */}
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {filteredOffices.length === 0 ? (
                <div className="p-8 text-center text-slate-400 font-bold text-xs space-y-1">
                  <div className="text-xl">🔍</div>
                  <div className="font-extrabold text-slate-600 dark:text-slate-300">
                    {t('partner.no_match')}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {t('partner.no_match_hint')}
                  </div>
                </div>
              ) : (
                filteredOffices.map((o) => {
                  const isSelected = selectedId === o.id;
                  const isDebt = o.kind === 'debt';
                  const accNumDisplay = o.accountLabel;

                  return (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`partner-office-option-${o.id}`}
                      onClick={() => {
                        onSelect(o.id);
                        setIsOpen(false);
                      }}
                      className={`w-full text-right px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#161b22]/70 transition-all flex items-center justify-between gap-3 ${
                        isSelected
                          ? 'bg-primary-50/70 dark:bg-primary-900/20 border-r-4 border-primary-500'
                          : ''
                      }`}
                    >
                      {/* Right Details: Name + Type Badge + Account + Phone */}
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-xs text-slate-900 dark:text-white truncate">
                            {o.label}
                          </span>
                          <span
                            className={`text-[9px] font-black px-2 py-0.5 rounded-md border ${
                              isDebt
                                ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200 dark:border-rose-900/50'
                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50'
                            }`}
                          >
                            {isDebt ? t('partner.debt_to_us') : t('partner.trust_by_us')}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                            #{accNumDisplay}
                          </span>
                        </div>

                        {o.phone && (
                          <div className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
                            <Phone size={10} className="text-slate-400" />
                            <span>{o.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Left: Customer Balance */}
                      <div className="text-left font-mono shrink-0 pl-1">
                        <div className="text-[9px] text-slate-400 font-bold mb-0.5">
                          {t('partner.account_balance')}
                        </div>
                        <div
                          className={`font-black text-xs ${
                            isDebt
                              ? 'text-rose-600 dark:text-rose-400'
                              : 'text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          {o.balanceLabel}{' '}
                          <span className="text-[10px] font-normal">د.ع</span>
                        </div>
                        {o.usdBalanceLabel && (
                          <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                            ${o.usdBalanceLabel}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
